import useSWR from 'swr';
import { useSWRList } from '@hooks/use-swr-list';
import { useCallback, useMemo } from 'react';
import { useShopifyCartContext } from '../onboarding/OnboardingFlowContainer/ShopifyCartProvider';

const getPlanTypesUrl = (cartId, petId) => {
  if (!cartId || !petId) return null;
  return `/api/v4/onboarding/${cartId}/pets/${petId}/plans/`;
};

const createMealPlanCombinationsUrl = (cartId, petId, mealplanId) => {
  return `/api/v4/onboarding/${cartId}/pets/${petId}/plans/${mealplanId}/`;
};

const PLAN_TYPE_KEYS = ['fresh', 'half_fresh', 'mixed', 'baked'];

export const useMealPlansCombinations = petId => {
  const { cartId } = useShopifyCartContext();

  // Fetch plan types directly
  const { data: planTypes, isLoading: isPlanTypesLoading } = useSWR(
    cartId && petId ? getPlanTypesUrl(cartId, petId) : null,
  );

  // Create URLs for each plan type and fetch data
  const { data, error, isValidating, mutate } = useSWRList(
    !!planTypes?.length && cartId && petId
      ? planTypes.map(planType =>
          createMealPlanCombinationsUrl(cartId, petId, planType.mealplan_id),
        )
      : [],
  );

  const combinationsByPlanType = useMemo(() => {
    const result = PLAN_TYPE_KEYS.reduce((acc, key) => {
      acc[key] = {
        planCombinations: [],
        availableRecipes: { fresh: [], baked: [] },
        cadence: null,
        recommendedCombinationId: null,
        recommendedCombination: null,
      };
      return acc;
    }, {});

    if (data && planTypes && Array.isArray(data)) {
      data.forEach((combinations, index) => {
        const planType = planTypes[index]?.plan_type_name;
        if (!planType) return;
        result[planType] = {
          planCombinations: combinations?.plan_combinations || [],
          availableRecipes: combinations?.available_recipes || {
            fresh: [],
            baked: [],
          },
          cadence: combinations?.cadence,
          recommendedCombinationId: combinations?.recommended_combination_id,
          recommendedCombination: combinations?.recommended_combination_id
            ? combinations?.plan_combinations?.find(
                plan =>
                  plan.combination_id ===
                  combinations.recommended_combination_id,
              )
            : combinations?.plan_combinations?.[0],
        };
      });
    }
    return result;
  }, [data, planTypes]);

  const refreshPlans = useCallback(() => {
    mutate();
  }, [mutate]);
  return {
    combinationsByPlanType,
    error,
    isLoading: (cartId && petId && !data && !error) || isPlanTypesLoading,
    isValidating,
    refreshPlans,
  };
};
